import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import Loading from '../../../components/Loading'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightFromBracket } from '@fortawesome/free-solid-svg-icons'
import { triggerRerender } from '../../../redux/rerenderSlice';
import { selectDropdownOptions } from '../../../redux/dropdownSlice';
import { CurrencyFormat, DateFormat } from '../../../helpers/formatting';
import { Link, useNavigate } from 'react-router-dom';

export default function PaymentEntryModal() {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(null);
  const modal = useSelector(selectModal);
  const dropdownOptions = useSelector(selectDropdownOptions)
  const [loans, setLoans] = useState(null);
  const request = useRequest()
  const record = modal.options?.record
  const navi = useNavigate()
  const active = modal.is_active && modal.type === 'payment_entry_modal'
  const requestedRecordId = useRef(null)

  const resetState = () => {
    requestedRecordId.current = null;
    setLoans(null);
    setIsLoading(null);
  };

  const handleCancel = () => {
    resetState();
    dispatch(setModal({ is_active: false }));
  };

  // This modal stays mounted in the Footer between openings, so drop the previous check entry's
  // loans before loading — otherwise the check can be assigned to the wrong customer's loan.
  useEffect(() => {
    resetState();
    if (active && record) {
      getPaymentsByLoanId(record)
    }
  }, [active, record?.id])

  useEffect(() => {
    return () => {
      dispatch(setModal({ is_active: false }));
      setLoans(null);
    };
  }, []);

  const getPaymentsByLoanId = async (record) => {
    requestedRecordId.current = record.id
    const isStale = () => requestedRecordId.current !== record.id
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${record.customer_id}/payment/unclosed?loanId=${record.loan_id}`)
    try {
      const response = await request.get()
      if (isStale()) return
      if (response.status === 200 || response.status === 201) {
        setLoans(response?.data?.data?.loans)
      } else {
        setLoans([])
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      if (isStale()) return
      setLoans([])
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const updatePaymentEntry = async (loanId, paymentId, index) => {
    if (!record || (isLoading != null && isLoading === index) || isLoading === 'loan') return;

    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/payment_check_entry/${record.id}/payment`);

      const body = {};
      if (loanId) {
        setIsLoading('loan');
        body.loan_id = loanId;
      } else {
        setIsLoading(paymentId);
        body.payment_id = paymentId;
      }

      const response = await request.modify(body);
      if (response.status === 200 || response.status === 201) {
        dispatch(setNotification({ type: 'success', body: '' }));
        dispatch(triggerRerender({ subpanelId: 'payment_check_entry' }));
        navi(`/payment_check_batch/${record.payment_check_batch_id}`, { state: { reloadPage: true } })
        handleCancel();
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }));
      }
      setIsLoading(null);
    } catch (error) {
      setIsLoading(null);
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme;
      dispatch(setNotification({ type: 'error', body: errorMessage }));
    }
  }

  const getData = () => {
    if (loans?.length > 0) {
      return loans?.map((loan, index) => {
        let loanGuid = loan.label;
        return (
          <div className="table-container px-1" key={loanGuid}>
            <table className="lox-hooktable-table table is-striped is-bordered is-narrow is-hoverable is-fullwidth is-grey mb-3">
              <thead>
              <tr>
                <th colSpan="7" className="is-selected has-text-left" style={{ backgroundColor: '#7F7F7F' }}>
                  Payment Schedule - Loan <Link to={`payment_schedule/${record.customer_id}?loan=${loanGuid}`} className="is-link" target="_blank" style={{ color: '#00abe7' }}>{loanGuid}</Link>&nbsp;&nbsp;Effective Date: <DateFormat dateString={loan?.effective_date}></DateFormat>
                </th>
                <th colSpan="2" className="is-selected has-text-right" style={{ backgroundColor: '#7F7F7F' }}>
                  <a style={{ color: '#00abe7' }} onClick={() => updatePaymentEntry(loan.id, null, null)}>
                    Create and Assign
                    {isLoading === 'loan' ? <Loading size="is-size-6" /> : <FontAwesomeIcon icon={faArrowRightFromBracket} />}
                  </a>
                </th>
              </tr>
              <tr>
                <th className="has-text-centered lox-payment-schedule-color">#</th>
                <th className="has-text-centered lox-payment-schedule-color">ID #</th>
                <th className="has-text-centered lox-payment-schedule-color">Scheduled Date</th>
                <th className="has-text-centered lox-payment-schedule-color">Status</th>
                <th className="has-text-centered lox-payment-schedule-color">Type</th>
                <th className="has-text-centered lox-payment-schedule-color">Principal $</th>
                <th className="has-text-centered lox-payment-schedule-color">Interest $</th>
                <th className="has-text-centered lox-payment-schedule-color">Total</th>
                <th className="has-text-centered lox-payment-schedule-color">Select</th>
              </tr>
              </thead>
              <tbody>
              {loan.unclosed_check_payments.length > 0 ? (
                loan.unclosed_check_payments.map((item, index) => {
                  return (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.guid}</td>
                      <td><DateFormat dateString={item.scheduled_date} /></td>
                      <td>{dropdownOptions.filter((o) => o.id === item?.payment_status_dropdown_option_id)[0].name}</td>
                      <td>{dropdownOptions.filter((o) => o.id === item?.payment_type_dropdown_option_id)[0].name}</td>
                      <td><CurrencyFormat value={item.principal_amount} showCurrencySign={false}></CurrencyFormat></td>
                      <td><CurrencyFormat value={item.interest_amount} showCurrencySign={false}></CurrencyFormat></td>
                      <td><CurrencyFormat value={item.scheduled_amount} showCurrencySign={false}></CurrencyFormat></td>
                      <td className="has-text-centered">
                        {isLoading === item.id ? (
                          <Loading size="is-size-6" />
                        ) : (
                          <FontAwesomeIcon
                            icon={faArrowRightFromBracket}
                            style={{ color: '#00abe7', cursor: 'pointer' }}
                            onClick={() => updatePaymentEntry(null, item.id, index)}
                          />
                        )}
                      </td>
                    </tr>
                  )
                })) : (
                <tr>
                  <td colSpan={9} className="has-text-centered has-text-grey">No unclosed check payments found. Either update a payment to payment method paper check or create & assign to a new payment.</td>
                </tr>
              )}
              </tbody>
            </table>
          </div>
        )
      })
    }
  }

  return modal.is_active && modal.type === 'payment_entry_modal' ? (
    <div className="modal is-active">
      <div className="modal-background"></div>
      <div
        className={`modal-card modal-card-payment-entry column ${loans?.length === 0 ? 'is-5' : 'is-9'}`}
        style={{ height: !loans || loans?.length === 0 ? '250px' : '' }}
      >
        <header className="modal-card-head py-3">
          <p className="modal-card-title is-capitalized">
            Payment Entry - Assignment
          </p>
          <button className="delete" aria-label="close" onClick={handleCancel}></button>
        </header>
        <section
          className="modal-card-body modal-card-body pt-1 pb-1"
          style={{ minHeight: "30px", overflow: 'hidden' }}
        >
          {!loans && (
            <div className="columns mb-0 is-flex-direction-column">
              <div className="column">
                <span className="has-text-center"><Loading /></span>
              </div>
            </div>
          )}
          {loans?.length > 0 && (
            <div className="columns mb-0 is-flex-direction-column">
              <div className="column">
                <h3>Below are the customer’s loan(s) that are allowed to have payment assignments. Choose the one that you would like to add this check entry to.<br />
                  <b>IMPORTANT: The Create & Assign button on each loan will create a new payment and assign this check entry to it. Only use this feature if there is not already a payment this check can be assigned to! Ensure you have checked the loan for any ACH payments that could be moved to paper check.</b>
                </h3>
              </div>
              <div className="column" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                <h3 style={{ marginRight: '5px' }}>
                  <span style={{ color: '#919FAB' }}>Customer:</span> {record.customer_label} |
                </h3>
                <h3 style={{ marginRight: '5px' }}>
                  <span style={{ color: '#919FAB' }}>Check #:</span> {record.check_number} |
                </h3>
                <h3 style={{ marginRight: '5px' }}>
                  <span style={{ color: '#919FAB' }}>Amount:</span> <CurrencyFormat value={record.amount}></CurrencyFormat> |
                </h3>
                <h3>
                  <span style={{ color: '#919FAB' }}>Postmarked Date:</span> <DateFormat dateString={record.postmarked_date}></DateFormat>
                </h3>
              </div>

              {getData()}
            </div>
          )}
        </section>
        <footer className="modal-card-foot is-justify-content-end py-1 pr-1">
          <div className="lox-form__buttons-wrapper has-text-right">
            <button type="button" className="button is-small has-background-grey has-text-white" onClick={handleCancel}>Cancel</button>
          </div>
        </footer>
      </div>
    </div>
  ) : null;
}
